import { useCallback, useEffect, useState } from 'react';
import api, { formatApiError } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  ShieldAlert, ShieldCheck, Loader2, RefreshCcw, AlertTriangle,
  CheckCircle2, Undo2,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtR = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ISSUE_META = {
  long_duration: { label: 'Duration > 24h', cls: 'bg-red-50 text-red-700 border-red-200' },
  end_before_start: { label: 'End before start', cls: 'bg-red-50 text-red-700 border-red-200' },
  still_running: { label: 'Still running', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  missing_contractor_rate: { label: 'No contractor rate', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  zero_client_rate: { label: 'R0 client rate', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  zero_cost: { label: 'R0 cost', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  high_invoice: { label: 'High invoice', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  loss_making: { label: 'Loss-making', cls: 'bg-red-50 text-red-700 border-red-200' },
  zero_invoice_cost: { label: 'R0 invoice, has cost', cls: 'bg-red-50 text-red-700 border-red-200' },
  closed_no_assignments: { label: 'No contractors', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  duplicate_vehicle_day: { label: 'Possible duplicate', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-ZA')} ${d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * BillingHealthPanel — admin/reviewer sanity scan across the period.
 * Rule-based (zero AI cost): timing slips, rate gaps, R0 costs, high invoices,
 * loss-makers, duplicates — with a per-case "Reviewed" sign-off for the reviewer.
 */
export const BillingHealthPanel = ({ start, end }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState('10000');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (thr) => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/reports/billing-health', {
        params: { start, end, threshold: Number(thr ?? threshold) || 10000 },
      });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [start, end, threshold]);

  useEffect(() => { load(); }, [start, end]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleReview = async (row) => {
    setBusyId(row.incident_id);
    try {
      if (row.review) {
        await api.delete(`/reports/billing-health/${row.incident_id}/review`);
        toast.success(`${row.incident_no} moved back to outstanding`);
      } else {
        await api.post(`/reports/billing-health/${row.incident_id}/review`);
        toast.success(`${row.incident_no} marked as reviewed`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-10 justify-center" data-testid="billing-health-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Scanning billing for anomalies…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="billing-health-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Incidents scanned</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="bh-scanned">{data.scanned}</p>
        </CardContent></Card>
        <Card className={data.flagged ? 'border-red-200' : 'border-emerald-200'}><CardContent className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Flagged</p>
          <p className={`text-2xl font-bold ${data.flagged ? 'text-red-600' : 'text-emerald-600'}`} data-testid="bh-flagged">{data.flagged}</p>
        </CardContent></Card>
        <Card className="border-emerald-200"><CardContent className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Reviewed ✓</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="bh-reviewed">{data.reviewed}</p>
        </CardContent></Card>
        <Card className={data.outstanding ? 'border-amber-300' : ''}><CardContent className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Outstanding</p>
          <p className={`text-2xl font-bold ${data.outstanding ? 'text-amber-600' : 'text-slate-800'}`} data-testid="bh-outstanding">{data.outstanding}</p>
        </CardContent></Card>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-2xl">
          Rule-based checks — wrong end-dates (&gt;24h), reversed times, missing contractor rates,
          R0 costs on closed cases, invoices above the threshold, loss-makers, R0 invoices with cost,
          closed cases with no contractors, and same-vehicle-same-day duplicates.
        </p>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase text-slate-500">High-invoice threshold (R)</Label>
            <Input
              type="number" min="0" step="500" value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(e.target.value); }}
              className="h-8 w-32 mt-0.5"
              data-testid="bh-threshold-input"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} data-testid="bh-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1" />} Re-scan
          </Button>
        </div>
      </div>

      {data.flagged === 0 ? (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-6 flex items-center gap-3" data-testid="bh-all-clear">
            <ShieldCheck className="w-8 h-8 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-800">All clear — no billing anomalies found</p>
              <p className="text-sm text-emerald-700">Every incident in this period passed all checks.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="bh-rows">
          {data.rows.map((r) => (
            <Card key={r.incident_id} className={r.review ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.review
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      : <ShieldAlert className="w-4 h-4 text-red-500" />}
                    <span className="font-semibold text-sm text-[#0D5B69]">{r.incident_no}</span>
                    <span className="text-xs text-slate-500">{r.date} · {r.customer_name}</span>
                    <Badge variant="outline" className="text-[10px] capitalize">{(r.status || '').replace('_', ' ')}</Badge>
                    {r.review && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]" data-testid={`bh-reviewed-badge-${r.incident_no}`}>
                        Reviewed by {r.review.reviewed_by_name} · {fmtWhen(r.review.reviewed_at)}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-600">
                      Invoice <b>{fmtR(r.client_subtotal)}</b> · Cost <b>{fmtR(r.contractor_total)}</b>
                    </span>
                    <Button
                      variant={r.review ? 'ghost' : 'outline'} size="sm"
                      className={r.review ? 'text-slate-500 h-7' : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50 h-7'}
                      disabled={busyId === r.incident_id}
                      onClick={() => toggleReview(r)}
                      data-testid={`bh-review-btn-${r.incident_no}`}
                    >
                      {busyId === r.incident_id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : r.review
                          ? <><Undo2 className="w-3.5 h-3.5 mr-1" /> Undo</>
                          : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark reviewed</>}
                    </Button>
                  </div>
                </div>
                <div className="space-y-1">
                  {r.issues.map((iss, i) => {
                    const meta = ISSUE_META[iss.type] || { label: iss.type, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                    return (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <Badge className={`${meta.cls} border text-[10px] flex-shrink-0`}>{meta.label}</Badge>
                        <span className="text-slate-700 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" /> {iss.detail}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BillingHealthPanel;
